
































































































































































































import { PropType } from 'vue';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { lowerCase } from 'lodash';
import {
  IEmployeeTimeOff,
  ITimeOffRequest,
  ITimeOffRequestData,
  TimeOffRequest,
  TimeOffStateEnum
} from '@/models';
import { ErrorManager } from '@/models/error';

import mixins from '@/utils/mixins';
import SnackbarMixin from '@/mixins/snackbar';
import auth from '@/mixins/auth';
import Functions from '@/mixins/functions';

import { IFetchTimeOffRequestPayload } from '@/services/timeoff-request';

import TimeOffRequestDetailMenuCard from './TimeOffRequestDetailMenuCard.vue';
import ErrorAlert from '../common/ErrorAlert.vue';
import TimeOffCalendarEvent from './TimeOffCalendarEvent.vue';

import { $ as $element } from '@/utils/helpers';

import { VCalendar } from 'vuetify/lib/components';
import {
  CalendarDaySlotScope,
  CalendarEventParsed,
  CalendarTimestamp
} from 'vuetify/types';
import { waitForReadystate } from '@/utils/componentHelpers';

const baseMixins = mixins(SnackbarMixin, Functions, auth);

interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    files: HTMLInputElement;
    vcalendar: InstanceType<typeof VCalendar> & {
      prev: () => void;
      next: () => void;
      checkChange: () => void;
      lastStart: CalendarTimestamp;
      lastEnd: CalendarTimestamp;
      getVisibleEvents(): CalendarEventParsed[];
      parsedEvents: CalendarEventParsed[];
    };
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    components: {
      TimeOffRequestDetailMenuCard,
      ErrorAlert,
      TimeOffCalendarEvent
    },

    props: {
      type: String,
      focus: String,
      externalErrorMessageDetail: String,
      externalErrorMessage: String,
      isLoading: Boolean,
      showRequestApprovalList: Boolean,
      params: Object as PropType<Partial<IFetchTimeOffRequestPayload>>,
      calendarSelectedList: Array as PropType<number[]>
    },

    data() {
      return {
        TimeOffStateEnum,
        loading: this.isLoading,
        deleteTimeOffDialog: false,

        selectedTimeOff: {} as ITimeOffRequest,
        selectedElement: null,
        selectedOpen: false,

        showClickMore: false,
        clickMoreNode: null as HTMLDivElement | null,
        nudgeLeft: 0,
        moreCardWidth: 300,
        clickMoreDayData: null as CalendarDaySlotScope | null,

        dateParams: {
          start: '',
          end: ''
        }
      };
    },

    computed: {
      ...mapGetters({
        getCalendarTimeOffs: 'timeOffRequest/getCalendarTimeOffs',
        getEmployeeTimeOff: 'employeeTimeOff/getEmployeeTimeOff',
        unapprovedTimeOffs: 'timeOffRequest/getUnapprovedEmployeesTimeOff',
        getTimeOffRequestById: 'timeOffRequest/getTimeOffRequestById',
        getTimeOffCalendarById: 'timeOffCalendar/getTimeOffCalendarById'
      }),

      calendarTimeOffs(): ITimeOffRequest[] {
        if (!this.calendarSelectedList) return [];
        return this.getCalendarTimeOffs(this.calendarSelectedList);
      },

      calendarFocus: {
        get(): string {
          return this.focus;
        },
        set(value: string) {
          this.$emit('update:focus', value);
        }
      },

      errorMessage: {
        get(): string {
          return this.externalErrorMessage;
        },
        set(val: string) {
          this.$emit('update:externalErrorMessage', val);
        }
      },

      errorMessageDetail: {
        get(): string {
          return this.externalErrorMessageDetail;
        },
        set(val: string) {
          this.$emit('update:externalErrorMessageDetail', val);
        }
      },

      calendarTimeOffList(): ITimeOffRequest[] {
        const list = this.showRequestApprovalList
          ? this.unapprovedTimeOffs
          : this.calendarTimeOffs;
        return list.map((timeOff: ITimeOffRequest) => {
          return new TimeOffRequest({
            ...timeOff,
            start: moment(timeOff.start)
              .utc(false)
              .format('YYYY-MM-DD HH:mm'),
            end: moment(timeOff.end)
              .utc(false)
              .format('YYYY-MM-DD HH:mm')
          });
        });
      },

      timeOffDetailQueryParam: {
        get(): Record<string, string | (string | null)[]> {
          return this.$route.query;
        },
        set(query: { request: string; start: string }) {
          this.$router
            .replace({
              name: 'time-off-calendar',
              query: query
            })
            .catch((e: Error) => e); //catch navigation duplication error
        }
      },

      computedRefs(): any {
        return this.$refs;
      }
    },

    watch: {
      params: {
        handler() {
          this.debounceFetchTimeOffRequests({
            start: this.$refs.vcalendar.lastStart,
            end: this.$refs.vcalendar.lastEnd
          });
        },
        deep: true
      },

      calendarFocus() {
        this.clearTimeOffDetailQueryParam();
      }
    },

    methods: {
      ...mapActions({
        fetchTimeOffRequests: 'timeOffRequest/fetchTimeOffRequests'
      }),

      setRouteQueryFromTimeOff(timeoff: ITimeOffRequest) {
        this.timeOffDetailQueryParam = {
          request: String(timeoff.id),
          start: moment(timeoff.start).format('YYYY-MM-DD')
        };
      },

      async openCalendarEventMenuCard(timeoff: ITimeOffRequest) {
        switch (timeoff.state) {
          case TimeOffStateEnum.Rejected:
            this.createSnack({
              color: `${timeoff.getStateInfo().color} darken-1`,
              type: `${timeoff.getStateInfo().color}`,
              icon: 'mdi-calendar',
              right: true,
              useBtn: false,
              msg: `Time Off Request has been prevously ${lowerCase(
                timeoff.getStateInfo().display_name
              )}`,
              btnText: 'Ok',
              timeout: 6000
            });
            this.clearTimeOffDetailQueryParam();
            break;

          default: {
            await waitForReadystate();
            await new Promise<void>(resolve =>
              setTimeout(() => resolve(), 750)
            );

            const containerNode = $element(
              `[data-date="${moment(timeoff.start)
                .utc(false)
                .format('YYYY-MM-DD')}"][data-more]`
            );

            if (containerNode && containerNode?.style.display !== 'none') {
              /** this will directly make a callback to `onClickEventMore` through the calendar */
              containerNode?.click();

              // wait in time for duration of menu open animation
              await new Promise<void>(resolve =>
                setTimeout(() => resolve(), 300)
              );
              if (
                this.computedRefs[`timeOffCalendarEventMore-${timeoff.id}`][0]
              ) {
                this.computedRefs[
                  `timeOffCalendarEventMore-${timeoff.id}`
                ][0].internalValue = true;
              }
            } else {
              if (this.computedRefs[`timeOffCalendarEvent-${timeoff.id}`]) {
                this.computedRefs[
                  `timeOffCalendarEvent-${timeoff.id}`
                ].internalValue = true;
              }
            }

            break;
          }
        }
      },

      prev() {
        this.$refs.vcalendar.prev();
      },
      next() {
        this.$refs.vcalendar.next();
      },

      openFormDialog(event: ITimeOffRequest) {
        this.$emit('openFormDialog', event);
      },

      clearAllErrors() {
        this.errorMessage = '';
        this.errorMessageDetail = '';
      },

      rebuild() {
        this.$refs.vcalendar.checkChange();
      },

      getCalendarInfo(event: ITimeOffRequest): string {
        const country = this.getTimeOffCalendarById(event.calendar).country;

        const calendarInfo = `${event.name} ${
          country ? `(${country.code})` : ''
        }`;

        return calendarInfo;
      },

      /**
       * @public api
       * To be used outside component as a reference
       */
      getCustomFilterParams(): IFetchTimeOffRequestPayload {
        const queryParams: IFetchTimeOffRequestPayload = {
          ...this.dateParams
        };

        if (queryParams.search && this.isUserHRPersonnel) {
          queryParams.all = 'true';
        }
        return queryParams;
      },

      debounceFetchTimeOffRequests({
        start,
        end
      }: {
        start: CalendarTimestamp;
        end: CalendarTimestamp;
      }) {
        this.clearAllErrors();
        this.loading = true;

        return this.debounce(this.genTimeOffRequests, 200)({ start, end });
      },

      genTimeOffRequests({
        start,
        end
      }: {
        start: CalendarTimestamp;
        end: CalendarTimestamp;
      }) {
        this.dateParams = {
          start: moment(start.date)
            .startOf('day')
            .format('YYYY-MM-DDTHH:mm:ss'),
          end: moment(end.date)
            .endOf('day')
            .format('YYYY-MM-DDTHH:mm:ss')
        };

        const queryParams = { ...this.params, ...this.dateParams };

        if (queryParams.search && this.isUserHRPersonnel) {
          queryParams.all = 'true';
        }

        this.fetchTimeOffRequests(queryParams)
          .catch(error => {
            if (error.response && error.response.data.detail) {
              this.errorMessageDetail = error.response.data.detail;
            }
            this.errorMessage = ErrorManager.extractApiError(error);
          })
          .then(() => {
            if (this.timeOffDetailQueryParam['request']) {
              const timeOff = this.getTimeOffRequestById(
                Number(this.timeOffDetailQueryParam['request'])
              );
              if (timeOff) {
                this.openCalendarEventMenuCard(timeOff);
              }
            }
          })
          .finally(() => {
            this.loading = false;
          });
      },

      onClickEventMore(event: CalendarDaySlotScope, mouseEvent: PointerEvent) {
        this.clickMoreDayData = event;
        mouseEvent.preventDefault();

        // attach menu to current calendar day node
        this.clickMoreNode = (mouseEvent.target as HTMLDivElement)
          .parentNode as HTMLDivElement;
        const rect: DOMRect = this.clickMoreNode?.getBoundingClientRect();

        // center menu card in day
        this.nudgeLeft = (this.moreCardWidth - rect.width) / 2;

        // render after the node is attached to the menu
        setTimeout(() => {
          this.showClickMore = true;
        }, 50);
      },

      closeClickMoreMenu() {
        this.showClickMore = false;
        this.clickMoreNode = null;
        this.clickMoreDayData = null;
      },

      getDayIdentifier(timestamp: {
        year: number;
        month: number;
        day: number;
      }): number {
        const OFFSET_YEAR = 10000;
        const OFFSET_MONTH = 100;

        return (
          timestamp.year * OFFSET_YEAR +
          timestamp.month * OFFSET_MONTH +
          timestamp.day
        );
      },
      isEventOn(event: CalendarEventParsed, dayIdentifier: number): boolean {
        return (
          dayIdentifier >= event.startIdentifier &&
          dayIdentifier <= event.endIdentifier
        );
      },

      isEventOnDay(event: CalendarEventParsed, dayIdentifier: number): boolean {
        return (
          dayIdentifier >= event.startIdentifier &&
          dayIdentifier <= event.endIdentifier
        );
      },

      isEventStart(
        event: CalendarEventParsed,
        day: CalendarTimestamp,
        dayIdentifier: number,
        firstWeekday: number
      ): boolean {
        return (
          dayIdentifier === event.startIdentifier ||
          (firstWeekday === day.weekday && this.isEventOn(event, dayIdentifier))
        );
      },

      getTimeoffRequestsForDay(day: CalendarDaySlotScope): ITimeOffRequest[] {
        const identifier = this.getDayIdentifier(day);
        const firstWeekday = 0; // for monday

        return this.$refs.vcalendar.parsedEvents
          .filter(
            event =>
              this.isEventStart(event, day, identifier, firstWeekday) ||
              this.isEventOnDay(event, identifier)
          )
          .map((timeOff: CalendarEventParsed) => {
            return new TimeOffRequest({
              ...timeOff.input
            } as ITimeOffRequestData);
          });
      },

      clearTimeOffDetailQueryParam() {
        // clear the timeoff detail query in route
        this.$router
          .replace({
            name: 'time-off-calendar'
          })
          .catch((e: Error) => e);
      }
    }
  });
